import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import EditorLayout from '../../../../components/Layouts/EditorLayout';
import {CommunityGetSchema} from '../../../../api/DBSchema/Community';
import {SMCSelect, SMCOption} from '../../../../components/Simple/SMCSelect';
import {SMCCheckbox} from '../../../../components/Simple/SMCCheckbox';
import SMCLabel from '../../../../components/Simple/SMCLabel';
import Sepperator from '../../../../components/Simple/Sepperator';
import Indentation from '../../../../components/Simple/Indentation';
import rightArrowIcon from '../../../../assets/images/right_arrow.png';
import RowLayout from '../../../../components/Layouts/RowLayout';
import SMCTextInput from '../../../../components/Simple/SMCTextInput';
import SMCButton from '../../../../components/Simple/SMCButton';
import {SMCRadio, SMCRadioGroup} from '../../../../components/Simple/SMCRadio';
import SetGatewayModal from '../../../PopupPages/SetGatewayModal';
import {useBoolean} from '../../../../utils/hooks';

const pageSchema = CommunityGetSchema;

function CommunityVPNSettings({
    communityObject,
    setCommunityData,
    setEditorPageId
}) {
    const { t } = useTranslation();

    const pageData = communityObject;
    const setPageValue = useCallback((paramName, value) => {
        setCommunityData(paramName, value);
    }, [setCommunityData]);

    const topologyType               = pageData[pageSchema.topologyType];
    const isPermanentTunnel          = pageData[pageSchema.isPermanentTunnel];
    const isBypassNat                = pageData[pageSchema.isBypassNat];
    const encryptionMethod           = pageData[pageSchema.encryptionMethod];

    // Star settings
    const centerGatewayId            = pageData[pageSchema.centerGatewayId];
    const satelliteRouting           = pageData[pageSchema.satelliteRouting];

    const topologyTypeValues         = pageSchema.topologyTypeValues;
    const encryptionMethodValues     = pageSchema.encryptionMethodValues;
    const satelliteRoutingValues     = pageSchema.satelliteRoutingValues;

    const [isSetCenterGatewayModalOpen, openSetCenterGatewayModal, closeSetCenterGatewayModal] = useBoolean(false);

    const handleChooseCenterGateway = useCallback(chosenGateways => {
        if (chosenGateways && chosenGateways.length) {
            setPageValue(pageSchema.centerGatewayId, chosenGateways[0]);
        } else {
            setPageValue(pageSchema.centerGatewayId, '');
        }
        closeSetCenterGatewayModal();
    }, [setPageValue, closeSetCenterGatewayModal]);


    return (
        <EditorLayout>
            {/* Topology */}
            <SMCLabel bold>{t('SMP.COMMUNITIES.TABLE.VPN_SETTINGS.TOPOLOGY')}</SMCLabel>
            <Sepperator />
            <Indentation>
                <SMCSelect
                    value={topologyType}
                    onChange={e => setPageValue(pageSchema.topologyType, e.target.value)}
                    label={t('SMP.COMMUNITIES.TABLE.VPN_SETTINGS.VPN_TYPE')}
                    tooltip={t('SMP.COMMUNITIES.TABLE.VPN_SETTINGS.VPN_TYPE.TOOLTIP')}
                >
                    <SMCOption value={topologyTypeValues.fullMesh}>{t('SMC_COMMUNITY_DVPN_TYPE_FULL_MESH')}</SMCOption>
                    <SMCOption value={topologyTypeValues.star}>{t('SMC_COMMUNITY_DVPN_TYPE_STAR')}</SMCOption>
                </SMCSelect>
                {/* Star settings */}
                {
                    topologyType === topologyTypeValues.star && (
                        <>
                        <RowLayout>
                            <SMCLabel useContextWidth tooltip={t('SMP.COMMUNITIES.TABLE.VPN_SETTINGS.CENTER_GW.TOOLTIP')}>
                                {t('SMP.COMMUNITIES.TABLE.VPN_SETTINGS.CENTER_GW')}
                            </SMCLabel>
                            <SMCTextInput value={centerGatewayId} onChange={e => setPageValue(pageSchema.centerGatewayId, e.target.value)} />
                            <SMCButton onClick={openSetCenterGatewayModal}>{t('SMP.COMMUNITIES.TABLE.VPN_SETTINGS.CHOOSE')}</SMCButton>
                        </RowLayout>
                        <RowLayout>
                            <SMCLabel useContextWidth>
                                {t('SMP.COMMUNITIES.TABLE.VPN_SETTINGS.SATELLITE_ROUTING')}
                            </SMCLabel>
                            <SMCRadioGroup
                                value={satelliteRouting}
                                onChange={e => setPageValue(pageSchema.satelliteRouting, e.target.value)}
                            >
                                <SMCRadio
                                    label={t('SMP.COMMUNITIES.TABLE.VPN_SETTINGS.RADIO_LINE1')}
                                    value={satelliteRoutingValues.NO_SATELLITE_COMMUNICATION}
                                />
                                <SMCRadio
                                    label={t('SMP.COMMUNITIES.TABLE.VPN_SETTINGS.RADIO_LINE2')}
                                    value={satelliteRoutingValues.THROUGH_CENTER}
                                />
                                <SMCRadio
                                    label={t('SMP.COMMUNITIES.TABLE.VPN_SETTINGS.RADIO_LINE3')}
                                    value={satelliteRoutingValues.INTERNET_THROUGH_CENTER}
                                />
                            </SMCRadioGroup>
                        </RowLayout>
                        </>
                    )
                }
                {/* Permanent tunnel */}
                <SMCCheckbox
                    checked={isPermanentTunnel}
                    onChange={e => setPageValue(pageSchema.isPermanentTunnel, e.target.checked)}
                    label={t('SMP.COMMUNITIES.TABLE.VPN_SETTINGS.PERMANENT_TUNNEL')}
                    labelPosition="start"
                    tooltip={t('SMP.COMMUNITIES.TABLE.VPN_SETTINGS.PERMANENT_TUNNEL.TOOLTIP')}
                />
                <SMCLabel><b>Note: </b>Permanent VPN tunnels are turned off even if enabled, for a community member who turn on the “Use DNS name”</SMCLabel>
            </Indentation>
            {/* Firewall */}
            <SMCLabel bold>{t('SMP.COMMUNITIES.TABLE.VPN_SETTINGS.FIREWALL')}</SMCLabel>
            <Sepperator />
            <Indentation>
                <SMCCheckbox
                    checked={isBypassNat}
                    onChange={e => setPageValue(pageSchema.isBypassNat, e.target.checked)}
                    label={t('SMP.COMMUNITIES.TABLE.VPN_SETTINGS.DISABLE_NAT')}
                    tooltip={t('SMP.COMMUNITIES.TABLE.VPN_SETTINGS.DISABLE_NAT.TOOLTIP')}
                    labelPosition="start"
                />
            </Indentation>
            <SMCLabel bold>{t('SMP.COMMUNITIES.TABLE.VPN_SETTINGS.ENCRYPTION')}</SMCLabel>
            <Sepperator />
            <Indentation>
                <SMCSelect
                    value={encryptionMethod}
                    onChange={e => setPageValue(pageSchema.encryptionMethod, e.target.value)}
                    label={t('SMP.COMMUNITIES.TABLE.VPN_SETTINGS.ENCRYPTION_METHOD')}
                >
                    <SMCOption value={encryptionMethodValues.ikev1}>IKEv1</SMCOption>
                    <SMCOption value={encryptionMethodValues.ikev2}>IKEv2</SMCOption>
                    <SMCOption value={encryptionMethodValues.preferIkev2SupportIkev1}>
                        {t('SMP.COMMUNITIES.TABLE.VPN_SETTINGS.ENCRYPTION_METHOD.PREFER')} IKEv2, {t('SMP.COMMUNITIES.TABLE.VPN_SETTINGS.ENCRYPTION_METHOD.SUPPORT')} IKEv1
                    </SMCOption>
                </SMCSelect>
            </Indentation>
            <Sepperator />
            <SMCLabel>{t('SMP.COMMUNITIES.TABLE.VPN_SETTINGS.LINE1')}</SMCLabel>
            <Indentation>
                <SMCLabel isLink={true} style={{ textDecoration: 'underline' }} onClick={() => setEditorPageId('ikeSettings')}><img src={rightArrowIcon} alt="" />{t('SMP.COMMUNITIES.TABLE.INNER_MENU.IKE_SETTINGS')}</SMCLabel>
            </Indentation>
            {/* Popup windows */}
            <SetGatewayModal
                isOpen={isSetCenterGatewayModalOpen}
                onRequestClose={closeSetCenterGatewayModal}
                onSuccess={handleChooseCenterGateway}
                selectMultiple={false}
            />
        </EditorLayout>
    )
}

export default CommunityVPNSettings;